import type { WalletMovementStatus } from '@erp/domain/pro';
import type { TypedSupabaseClient } from '../client';
import type {
  IProWalletRepository,
  ProWalletBalance,
  ProWalletFilters,
  ProWalletMovement,
} from '../../../interfaces/src';

export class SupabaseProWalletRepository implements IProWalletRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getBalance(): Promise<ProWalletBalance> {
    const { data: userData } = await this.supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return { balance: 0, pendingCommission: 0, approvedCommission: 0 };

    const { data: customer } = await (this.supabase as any)
      .from('pro_customers')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();
    if (!customer) return { balance: 0, pendingCommission: 0, approvedCommission: 0 };

    const { data, error } = await (this.supabase as any)
      .from('pro_customer_wallet_view')
      .select('*')
      .eq('pro_customer_id', customer['id'] as string)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return { balance: 0, pendingCommission: 0, approvedCommission: 0 };
      throw error;
    }
    if (!data) return { balance: 0, pendingCommission: 0, approvedCommission: 0 };
    return {
      balance: Number(data['balance'] ?? 0),
      pendingCommission: Number(data['pending_commission'] ?? 0),
      approvedCommission: Number(data['approved_commission'] ?? 0),
    };
  }

  async listMovements(filter?: ProWalletFilters): Promise<ProWalletMovement[]> {
    let query = (this.supabase as any).from('pro_wallet_movements').select('*');
    if (filter?.status) query = query.eq('status', filter.status);
    if (filter?.type) query = query.eq('type', filter.type);
    if (filter?.from) query = query.gte('date', filter.from);
    if (filter?.to) query = query.lte('date', filter.to);
    const { data, error } = await query.order('date', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapMovement);
  }

  async setMovementStatus(id: string, status: WalletMovementStatus): Promise<ProWalletMovement> {
    const { data, error } = await (this.supabase as any)
      .from('pro_wallet_movements')
      .update({ status })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapMovement(data);
  }
}

function mapMovement(row: Record<string, unknown>): ProWalletMovement {
  return {
    id: row['id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    amount: Number(row['amount']),
    type: row['type'] as ProWalletMovement['type'],
    status: row['status'] as ProWalletMovement['status'],
    referenceCode: (row['reference_code'] as string) ?? null,
    note: (row['note'] as string) ?? null,
    date: row['date'] as string,
    createdAt: row['created_at'] as string,
  };
}
